import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useMemo, useContext, useRef } from 'react';
// @mui
import {
  Card,
  Container,
  Stack,
  Button,
  Box,
  Autocomplete,
  TextField,
  MenuItem
} from '@mui/material';
import MUIDataTable from "mui-datatables";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { id } from "date-fns/locale";
import { endOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import MonitoringNominalFooter from '../../components/table/MonitoringNominalFooter';
import { TableLoader } from '../../components/table';
import { getCurrentDate } from "../../utils/formatDate";
// Context
import { axiosPrivate } from '../../common/axiosPrivate';
import AuthContext from '../../context/authContext';

import { filterMonitoringPenagihan } from '../../utils/filterMonitoringPenagihan';

export default function PenagihanEksternalPage() {
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const { user } = useContext(AuthContext);
  const [data, setData] = useState([]);
  const [transferFilter, setTransferFilter] = useState('semua');
  const [rumpunFilter, setRumpunFilter] = useState('semua');
  const visibleData = useMemo(
    () => filterMonitoringPenagihan(data, transferFilter, rumpunFilter),
    [data, transferFilter, rumpunFilter]
  );
  const [tableLoading, setTableLoading] = useState(false);
  const [petugas, setPetugas] = useState("");
  const [petugasList, setPetugasList] = useState([]);

  const BodyComponent = useMemo(
    () => (props) => (
      <TableLoader loading={tableLoading} {...props} />
    ),
    [tableLoading]
  );

  const columns = [
    {
      name: "nomorInvoice",
      label: "Nomor Invoice",
      options: {
        filter: false,
        sort: true,
        display: false,
        customBodyRender: (value) => value || '-'
      }
    },
    {
      name: "number",
      label: "No",
      options: {
        filter: false,
        sort: false,
        customBodyRender : (value, tableMeta) => {
          const rowIndex = Number(tableMeta.rowIndex) + 1;
          return rowIndex;
        }
      },
    },
    {
      name: "kantor",
      label: "Kantor",
      options: {
       filter: true,
       sort: true,
       customBodyRender: (value) => value?.nama
      }
    },
    {
      name: "rumpun",
      label: "Rumpun",
      options: {
       filter: true,
       sort: true,
       customBodyRender: (value) => value?.nama
      }
    },
    {
      name: "anggota",
      label: "Anggota",
      options: {
       filter: true,
       sort: true,
       customBodyRender: (value) => value?.nama
      }
    },
    {
      name: "petugas",
      label: "Petugas",
      options: {
       filter: true,
       sort: true,
       customBodyRender: (value) => value?.nama
      }
    },
    {
      name: "tanggal",
      label: "Tanggal",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => formatInTimeZone(new Date(value), 'UTC', 'dd/MM/yyyy HH:mm')
      }
    },
    {
      name: "transfer",
      label: "Transfer",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => value === true ? 'Ya' : 'Tidak'
      }
    },
    {
      name: "nominal",
      label: "Nominal",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const number = Number(value);
          if (!value || Number.isNaN(number) || number === 0) return "-";
          return number.toLocaleString("id-ID"); // hasil: 1.000.000
        }
      }
    },
    {
      name: "kendala",
      label: "Kendala",
      options: {
        filter: true,
        sort: true,
        display: false
      }
    },
    {
      name: "operatorJaringan",
      label: "Operator Jaringan",
      options: {
        filter: true,
        sort: true,
        display: false
      }
    },
    {
      name: "keterangan",
      label: "Keterangan",
      options: {
        filter: true,
        sort: true,
        display: false
      }
    },
    {
      name: "_id",
      label: "Aksi",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <Button
            color="error"
            variant="outlined"
            size="small"
            onClick={() => handleDelete(value)}
          >
            Hapus
          </Button>
        )
      }
    },
  ];
   
  const options = {
    filterType: "dropdown",
    selectableRowsHideCheckboxes: true,
    customTableBodyFooterRender: ({ data: displayData, columns: tableColumns }) => (
      <MonitoringNominalFooter displayData={displayData} rows={visibleData} columns={tableColumns} />
    ),
    downloadOptions: {
      filename: `Absen-${user?.kantorName}-${getCurrentDate('-')}.csv`,
      filterOptions: {
        useDisplayedColumnsOnly: false,
        useDisplayedRowsOnly: true,
      }
    },
  };
  
  const handleStartDateChange = (date) => {
    startDateRef.current = date;
  };

  const handleEndDateChange = (date) => {
    endDateRef.current = date;
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus data penagihan ini?')) return;

    try {
      await axiosPrivate.delete(`/penagihan/${id}`);
      setData((currentData) => currentData.filter((item) => item._id !== id));
    } catch (error) {
      console.error('Gagal menghapus data penagihan:', error);
    }
  };

  const filterData = () => {
    setTableLoading(true);

    const modEndDate = endOfDay(endDateRef.current);

    // Compose payload sesuai role
    const payload = {
      startDate: startDateRef.current,
      endDate: modEndDate,
      petugas
    };

    axiosPrivate.post('/laporan/monitoring-penagihan-mis', payload)
      .then((response) => {
        setData(response?.data);
      })
      .catch((error) => console.log(error))
      .finally(() => setTableLoading(false));
  };

  useEffect(() => {
    const fetchPetugasList = async () => {
      try {
        const response = await axiosPrivate.get("/penagihan/dropdown-eksternal", {
          params: {
            kantorList: user.kantorList.join(',')
          }
        });

        setPetugasList(response.data || []);
      } catch (error) {
        console.error("Error fetching petugas list:", error);
      }
    };
    fetchPetugasList();
  }, [user?.kantor, user?.role, user?.kantorList]);

  return (
    <>
      <Helmet>
        <title> Monitoring Penagihan | Sip App </title>
      </Helmet>
      <Container
        sx={{
          mt: 5
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 2
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="start" sx={{ flexWrap: 'wrap', rowGap: 2 }}>
            <Box sx={{ marginRight: 2 }}>
              <LocalizationProvider adapterLocale={id} dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Dari Tanggal"
                  value={startDateRef.current}
                  onChange={handleStartDateChange}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ marginRight: 2 }}>
              <LocalizationProvider adapterLocale={id} dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Sampai Tanggal"
                  value={endDateRef.current}
                  onChange={handleEndDateChange}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ marginRight: 2 }}>
              <Autocomplete
                options={petugasList}
                getOptionLabel={(option) => option.nama}
                value={petugasList.find((p) => p.id === petugas) || null}
                onChange={(_, newValue) => setPetugas(newValue ? newValue.id : "")}
                sx={{ width: 240, height: 40 }}
                renderInput={(params) => (
                  <TextField {...params} label="Petugas" size="small" />
                )}
              />
            </Box>

            <TextField
              select
              label="Transfer"
              size="small"
              value={transferFilter}
              onChange={(event) => setTransferFilter(event.target.value)}
              sx={{ minWidth: 130, mr: 2 }}
            >
              <MenuItem value="semua">Semua</MenuItem>
              <MenuItem value="ya">Ya</MenuItem>
              <MenuItem value="tidak">Tidak</MenuItem>
            </TextField>
            <TextField
              select
              label="Produk"
              size="small"
              value={rumpunFilter}
              onChange={(event) => setRumpunFilter(event.target.value)}
              sx={{ minWidth: 150, mr: 2 }}
            >
              <MenuItem value="semua">Semua</MenuItem>
              <MenuItem value="UMP">UMP</MenuItem>
              <MenuItem value="UMIHA">UMIHA</MenuItem>
            </TextField>

            <Button variant="contained" onClick={filterData}>
              Filter
            </Button>
          </Stack>
        </Box>
        <Card>
          <MUIDataTable
            title="Monitoring Penagihan"
            data={visibleData}
            columns={columns}
            options={options}
            components={{ TableBody: BodyComponent }}
          />
        </Card>
      </Container>
    </>
  );
}
